import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle, Ban, Download, FileText, Loader2, RefreshCw, X,
} from 'lucide-react';
import { abrirArquivoComToken } from '../../../utils/arquivoComToken';
import { getEmpresa } from '../../../utils/empresas';

// =====================================================================
// FINANCEIRO — Cofrinho: a CONSULTA / CONSOLIDAÇÃO.
//
// Lista os lançamentos de uma competência, com o total a repassar no topo. A loja vê
// só a sua (recorte do servidor); o Financeiro vê a rede e a consolidação por
// loja — o "quanto cada loja tem para repassar ao Instituto Ronald" no mês.
//
// ⚠️ O RECORTE POR LOJA É DO SERVIDOR. Aqui o filtro de loja só ESTREITA o que o
//    backend já devolveu.
// ⚠️ CANCELADO É ESCONDIDO, NUNCA APAGADO; nunca entra nos totais.
// =====================================================================

const API = '/api/financeiro/cofrinho';
const COR = '#0d7a74';
const COR_FORTE = '#115e59';
const COR_FUNDO = '#f0fdfa';
const COR_BORDA = '#99f6e4';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });
const brl = (n) => Number(n || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataBr = (iso) => String(iso || '').split('-').reverse().join('/');
const mesBr = (comp) => {
  if (!/^\d{4}-\d{2}$/.test(String(comp || ''))) return String(comp || '');
  const [a, m] = comp.split('-');
  const nomes = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  return `${nomes[Number(m) - 1]}/${a}`;
};

const estiloInput = {
  padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8,
  fontSize: 14, color: '#0f172a', background: '#fff', fontFamily: 'inherit',
};
const th = {
  textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 800,
  color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3,
  borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap',
};
const td = { padding: '10px 12px', fontSize: 14, color: '#0f172a', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' };

const Placar = ({ rotulo, valor, detalhe, cor }) => (
  <div style={{
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px',
    borderLeft: `4px solid ${cor}`, minWidth: 0,
  }}>
    <div style={{ fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3 }}>
      {rotulo}
    </div>
    <div style={{ marginTop: 6, fontSize: 24, fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
      {valor}
    </div>
    {detalhe && <div style={{ marginTop: 2, fontSize: 13, color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{detalhe}</div>}
  </div>
);

const CofrinhoConsulta = () => {
  const [opcoes, setOpcoes] = useState(null);
  const [lojas, setLojas] = useState([]);
  const [competencias, setCompetencias] = useState([]);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const [filtros, setFiltros] = useState({ competencia: '', empresa: '', loja: '', incluirCancelados: false });

  const [cancelando, setCancelando] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [salvandoCancelamento, setSalvandoCancelamento] = useState(false);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const [rOpcoes, rLojas, rComp] = await Promise.all([
          fetch(`${API}/opcoes`, { headers: auth() }),
          fetch(`${API}/lojas`, { headers: auth() }),
          fetch(`${API}/competencias`, { headers: auth() }),
        ]);
        if (!vivo) return;
        if (rOpcoes.ok) setOpcoes(await rOpcoes.json());
        if (rLojas.ok) setLojas(await rLojas.json());
        if (rComp.ok) setCompetencias(await rComp.json());
      } catch { /* o carregar() abaixo reporta a falha */ }
    })();
    return () => { vivo = false; };
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const q = new URLSearchParams();
      if (filtros.competencia) q.set('competencia', filtros.competencia);
      if (filtros.empresa) q.set('empresa', filtros.empresa);
      if (filtros.loja) q.set('loja', filtros.loja);
      if (filtros.incluirCancelados) q.set('incluirCancelados', '1');

      const r = await fetch(`${API}?${q}`, { headers: auth() });
      if (!r.ok) throw new Error();
      setDados(await r.json());
    } catch {
      setErro('Não foi possível carregar os registros.');
    } finally {
      setCarregando(false);
    }
  }, [filtros]);

  useEffect(() => { carregar(); }, [carregar]);

  const confirmarCancelamento = async () => {
    setSalvandoCancelamento(true);
    setErro('');
    try {
      const r = await fetch(`${API}/${cancelando.ID}/cancelar`, {
        method: 'PUT',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ motivo }),
      });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.message || 'Não foi possível cancelar.');
      setCancelando(null);
      setMotivo('');
      carregar();
    } catch (ex) {
      setErro(ex.message);
    } finally {
      setSalvandoCancelamento(false);
    }
  };

  const verComprovante = async (url) => {
    try {
      await abrirArquivoComToken(`${API}/comprovante?url=${encodeURIComponent(url)}`, 'comprovante-cofrinho');
    } catch (ex) {
      setErro(ex.message || 'Não foi possível abrir o comprovante.');
    }
  };

  // CSV montado no navegador, a partir das linhas na tela (o que se exporta é o
  // que se está vendo).
  const exportarCsv = () => {
    const linhas = dados?.registros || [];
    const cabecalho = ['ID', 'Loja', 'Data', 'Competencia', 'Banana', 'Valor',
      'Comprovante', 'Registrado por', 'Cancelado em', 'Motivo do cancelamento'];
    const escapar = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const corpo = linhas.map((r) => [
      r.ID, r.LOJA, dataBr(r.DATA_REGISTRO), r.COMPETENCIA, r.NUMERO_BANANA,
      String(Number(r.VALOR).toFixed(2)).replace('.', ','), // vírgula decimal p/ Excel PT-BR
      r.COMPROVANTE_URL ? 'Sim' : 'Nao',
      r.REGISTRADO_POR_NOME,
      r.CANCELADO_EM ? new Date(r.CANCELADO_EM).toLocaleString('pt-BR') : '',
      r.CANCELAMENTO_MOTIVO || '',
    ].map(escapar).join(';'));

    const blob = new Blob(['﻿' + [cabecalho.map(escapar).join(';'), ...corpo].join('\r\n')],
      { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `cofrinho_${dados?.competencia || ''}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const t = dados?.totais;
  const registros = dados?.registros || [];

  // Consolidação por loja (só faz sentido quando a pessoa vê mais de uma loja).
  // Calculada das linhas VÁLIDAS já na tela — é o "quanto cada loja tem a repassar".
  const porLoja = (() => {
    if (lojas.length <= 1) return [];
    const mapa = new Map();
    registros.filter((r) => !r.CANCELADO_EM).forEach((r) => {
      const at = mapa.get(r.LOJA) || { loja: r.LOJA, quantidade: 0, valor: 0 };
      at.quantidade += 1;
      at.valor += Number(r.VALOR || 0);
      mapa.set(r.LOJA, at);
    });
    return [...mapa.values()].sort((a, b) => b.valor - a.valor);
  })();

  // Empresas disponíveis, derivadas das lojas que a pessoa enxerga (o recorte já
  // veio do servidor na lista de lojas). getEmpresa reconhece o código canônico.
  const empresas = [...new Set(lojas.map(getEmpresa))].sort();

  // O seletor de LOJA respeita a EMPRESA escolhida — sem empresa, lista todas.
  const lojasDoFiltro = filtros.empresa
    ? lojas.filter((l) => getEmpresa(l) === filtros.empresa)
    : lojas;

  // Troca de empresa: se a loja escolhida não pertence mais a ela, zera a loja.
  const trocarEmpresa = (empresa) => setFiltros((f) => ({
    ...f,
    empresa,
    loja: empresa && f.loja && getEmpresa(f.loja) !== empresa ? '' : f.loja,
  }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 22 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Cofrinho — Consulta</h3>
          <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 14 }}>
            Os lançamentos por competência, com o total a repassar ao Instituto Ronald.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={carregar} title="Recarregar" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px',
            background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0',
            borderRadius: 8, fontWeight: 700, cursor: 'pointer',
          }}>
            <RefreshCw size={16} /> Atualizar
          </button>
          <button onClick={exportarCsv} disabled={!registros.length} style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px',
            background: registros.length ? COR_FUNDO : '#f1f5f9',
            color: registros.length ? COR_FORTE : '#94a3b8',
            border: `1px solid ${registros.length ? COR_BORDA : '#e2e8f0'}`,
            borderRadius: 8, fontWeight: 700, cursor: registros.length ? 'pointer' : 'default',
          }}>
            <Download size={16} /> Exportar CSV
          </button>
        </div>
      </div>

      {/* FILTROS */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 12,
        padding: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, marginBottom: 20,
      }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Competência</span>
          <select value={filtros.competencia || dados?.competencia || ''}
            onChange={(e) => setFiltros((f) => ({ ...f, competencia: e.target.value }))} style={estiloInput}>
            {competencias.map((c) => <option key={c} value={c}>{mesBr(c)}</option>)}
          </select>
        </label>

        {empresas.length > 1 && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Empresa</span>
            <select value={filtros.empresa} onChange={(e) => trocarEmpresa(e.target.value)} style={estiloInput}>
              <option value="">Todas</option>
              {empresas.map((emp) => <option key={emp} value={emp}>{emp}</option>)}
            </select>
          </label>
        )}

        {lojas.length > 1 && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Loja</span>
            <select value={filtros.loja} onChange={(e) => setFiltros((f) => ({ ...f, loja: e.target.value }))} style={estiloInput}>
              <option value="">Todas</option>
              {lojasDoFiltro.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9, cursor: 'pointer' }}>
          <input type="checkbox" checked={filtros.incluirCancelados}
            onChange={(e) => setFiltros((f) => ({ ...f, incluirCancelados: e.target.checked }))} />
          <span style={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>Mostrar cancelados</span>
        </label>
      </div>

      {erro && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: 14, marginBottom: 20,
          background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 14,
        }}>
          <AlertTriangle size={18} /> {erro}
        </div>
      )}

      {/* PLACAR — da competência filtrada, sempre SEM os cancelados. */}
      {t && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 22 }}>
          <Placar rotulo="Total a repassar" valor={brl(t.valor)} detalhe={`competência ${mesBr(dados?.competencia)}`} cor={COR} />
          <Placar rotulo="Lançamentos" valor={t.quantidade} cor="#0ea5e9" />
          <Placar rotulo="Registros cancelados" valor={t.cancelados}
            detalhe={filtros.incluirCancelados ? 'listados abaixo' : 'fora da lista'} cor="#94a3b8" />
        </div>
      )}

      {/* CONSOLIDAÇÃO POR LOJA (escritório: rede com mais de uma loja). */}
      {porLoja.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 22 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 10 }}>
            A repassar por loja — {mesBr(dados?.competencia)}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {porLoja.map((l) => (
              <div key={l.loja} style={{
                display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 14px',
                background: COR_FUNDO, border: `1px solid ${COR_BORDA}`, borderRadius: 8,
              }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: COR_FORTE }}>{l.loja}</span>
                <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{brl(l.valor)}</span>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>{l.quantidade} lançamento(s)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {t?.truncado && (
        <div style={{ padding: 12, marginBottom: 16, background: '#fef9c3', color: '#854d0e', borderRadius: 8, fontSize: 13 }}>
          A competência devolveu o teto de 2.000 registros — o placar conta só os listados.
        </div>
      )}

      {/* TABELA */}
      {carregando ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#64748b', padding: 24 }}>
          <Loader2 size={18} className="spin" /> Carregando…
        </div>
      ) : registros.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
          Nenhum lançamento registrado nesta competência.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead>
              <tr>
                <th style={th}>Data</th>
                <th style={th}>Loja</th>
                <th style={th}>Banana</th>
                <th style={{ ...th, textAlign: 'right' }}>Valor</th>
                <th style={{ ...th, textAlign: 'center' }}>Comprovante</th>
                <th style={th}>Registrado por</th>
                {opcoes?.podeCancelar && <th style={{ ...th, textAlign: 'center' }}>Ação</th>}
              </tr>
            </thead>
            <tbody>
              {registros.map((r) => {
                const cancelado = !!r.CANCELADO_EM;
                return (
                  <tr key={r.ID} style={{ background: cancelado ? '#f8fafc' : '#fff', opacity: cancelado ? 0.65 : 1 }}>
                    <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      {dataBr(r.DATA_REGISTRO)}
                      {cancelado && (
                        <div style={{ marginTop: 3, fontSize: 11, fontWeight: 800, color: '#b91c1c', textTransform: 'uppercase' }}
                          title={r.CANCELAMENTO_MOTIVO || ''}>
                          Cancelado
                        </div>
                      )}
                    </td>
                    <td style={{ ...td, fontWeight: 700 }}>{r.LOJA}</td>
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{r.NUMERO_BANANA}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      {brl(r.VALOR)}
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      {r.COMPROVANTE_URL ? (
                        <button onClick={() => verComprovante(r.COMPROVANTE_URL)} title="Abrir comprovante" style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px',
                          background: COR_FUNDO, color: COR_FORTE, border: `1px solid ${COR_BORDA}`,
                          borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer',
                        }}>
                          <FileText size={14} /> Ver
                        </button>
                      ) : (
                        <span style={{ color: '#cbd5e1' }}>—</span>
                      )}
                    </td>
                    <td style={{ ...td, fontSize: 13, color: '#64748b' }}>
                      {r.REGISTRADO_POR_NOME}
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        {r.REGISTRADO_EM ? new Date(r.REGISTRADO_EM).toLocaleString('pt-BR') : ''}
                      </div>
                    </td>
                    {opcoes?.podeCancelar && (
                      <td style={{ ...td, textAlign: 'center' }}>
                        {!cancelado && (
                          <button onClick={() => { setCancelando(r); setMotivo(''); }} title="Cancelar este registro" style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                            background: '#fff', color: '#b91c1c', border: '1px solid #fecaca',
                            borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer',
                          }}>
                            <Ban size={14} /> Cancelar
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* MODAL DE CANCELAMENTO */}
      {cancelando && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 26, width: '100%', maxWidth: 480 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <h4 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: '#0f172a' }}>
                Cancelar o registro nº {cancelando.ID}
              </h4>
              <button onClick={() => setCancelando(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                <X size={20} />
              </button>
            </div>

            <p style={{ margin: '0 0 16px', fontSize: 14, color: '#64748b', lineHeight: 1.55 }}>
              {cancelando.LOJA} · banana {cancelando.NUMERO_BANANA} · {brl(cancelando.VALOR)} · {dataBr(cancelando.DATA_REGISTRO)}
              <br />
              <span style={{ color: '#94a3b8', fontSize: 13 }}>
                O registro não é apagado — fica visível como cancelado, com seu nome e o motivo.
              </span>
            </p>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 18 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>
                Motivo do cancelamento <span style={{ color: '#dc2626' }}>*</span>
              </span>
              <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} maxLength={255}
                placeholder="Ex.: valor lançado em duplicidade."
                style={{ ...estiloInput, resize: 'vertical', width: '100%', boxSizing: 'border-box' }} />
              <span style={{ fontSize: 12, color: '#94a3b8' }}>{motivo.trim().length}/255 — mínimo 5 caracteres.</span>
            </label>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setCancelando(null)} style={{
                padding: '10px 18px', background: '#f1f5f9', color: '#475569',
                border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
              }}>
                Voltar
              </button>
              <button onClick={confirmarCancelamento}
                disabled={motivo.trim().length < 5 || salvandoCancelamento}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 18px',
                  background: motivo.trim().length < 5 ? '#cbd5e1' : '#b91c1c', color: '#fff',
                  border: 'none', borderRadius: 8, fontWeight: 700,
                  cursor: motivo.trim().length < 5 ? 'default' : 'pointer',
                }}>
                {salvandoCancelamento ? <><Loader2 size={16} className="spin" /> Cancelando…</> : <><Ban size={16} /> Confirmar cancelamento</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CofrinhoConsulta;
